import React from 'react';
import { Box, Typography, Chip, useTheme } from '@mui/material';

export interface DataFlowNode {
  id: string;
  label: string;
  value: number | string;
  sublabel?: string;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  onClick?: () => void;
}

export interface DataFlowDiagramProps {
  nodes: DataFlowNode[];
  title?: string;
  direction?: 'horizontal' | 'vertical';
  showArrows?: boolean;
}

export const DataFlowDiagram: React.FC<DataFlowDiagramProps> = ({
  nodes,
  title,
  direction = 'horizontal',
  showArrows = true,
}) => {
  const theme = useTheme();

  const isHorizontal = direction === 'horizontal';

  return (
    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
      {title && (
        <Typography
          variant="subtitle2"
          sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}
        >
          {title}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          flexWrap: 'wrap',
          flexDirection: isHorizontal ? 'row' : 'column',
        }}
      >
        {nodes.map((node, index) => (
          <React.Fragment key={node.id}>
            <Box
              sx={{
                textAlign: 'center',
                cursor: node.onClick ? 'pointer' : 'default',
                transition: 'transform 0.2s',
                '&:hover': node.onClick ? { transform: 'scale(1.05)' } : {},
              }}
              onClick={node.onClick}
            >
              <Chip
                label={`${node.value} ${node.label}`}
                color={node.color || 'primary'}
                sx={{
                  minWidth: 100,
                  height: 32,
                  fontWeight: 600,
                }}
              />
              {node.sublabel && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {node.sublabel}
                </Typography>
              )}
            </Box>
            {showArrows && index < nodes.length - 1 && (
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  transform: isHorizontal ? 'none' : 'rotate(90deg)',
                }}
              >
                →
              </Typography>
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default DataFlowDiagram;

